import React from 'react'
import { Input, Textarea } from '../../components/Input'
import { ProjectModel } from '../../utils/interfaces';
import { useAsyncEffect, useEventListener } from '../../utils/functions';
import { deleteItems, Admin, getItems, updateItem, getItem } from '../../api/API';
import { tables } from '../../utils/constants';
import AlertWrapper from '../../components/AlertWrapper';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProjectProps } from './Project';
import { Subtitle, Title } from '../../components/General';

const Config = (props: ProjectProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = React.useState<ProjectModel[]>([]);
  const [newName, setNewName] = React.useState(props.project.name);
  const [isSavingName, setIsSavingName] = React.useState(false);
  const [newDescription, setNewDescription] = React.useState(props.project.description);
  const [isSavingDescription, setIsSavingDescription] = React.useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  // load data
  useAsyncEffect(async () => {
    const data = await getItems(tables.projects, {'userId': props.user!.id });
    setProjects(data.items || []);
    setIsLoading(false);
  }, []);

  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.delete-project-alert') && !target.closest('.delete-project-button')) {
      setShowDeleteAlert(false);
    }
  }, []);

  // save updates
  const saveName = async () => {
    setIsSavingName(true);
    const newProject: ProjectModel = { ...props.project, name: newName, lastUpdate: new Date().toISOString() };
    await updateItem(tables.projects, newProject.id, { name: newName, lastUpdate: new Date().toISOString() });
    props.setProject(newProject);
    setProjects(projects.map(p => p.id === newProject.id ? newProject : p));
    setIsSavingName(false);
  }
  const saveDescription = async () => {
    setIsSavingDescription(true);
    const newProject: ProjectModel = { ...props.project, description: newDescription, lastUpdate: new Date().toISOString() };
    await updateItem(tables.projects, newProject.id, { description: newDescription, lastUpdate: new Date().toISOString() });
    props.setProject(newProject);
    setProjects(projects.map(p => p.id === newProject.id ? newProject : p));
    setIsSavingDescription(false);
  }

  const deleteProject = async () => {
    setIsDeleting(true);
    await deleteItems(tables.projects, [props.project.id]);
    // delete api key
    await Admin.deleteApiKey(props.project.id);
    // delete auth system
    const auth = (await getItem(tables.auths, `${props.project!.id}`))?.item;
    if (auth?.auth) {
      await Admin.deleteUserPool(auth.auth.userPoolId);
      await deleteItems(tables.auths, [`${props.project!.id}`]);
    }
    // delete databases
    const dbs = (await getItems(tables.databases, { 'projectId': props.project.id })).items || [];
    for (const db of dbs) {
      await Admin.deleteDatabase(db.id);
    }
    await deleteItems(tables.databases, dbs.map((d: any) => d.id));
    // delete storages & files
    const sts = (await getItems(tables.storages, { 'projectId': props.project.id })).items || [];
    await deleteItems(tables.storages, sts.map((s: any) => s.id));
    // delete functions
    const fns = (await getItems(tables.functions, { 'projectId': props.project.id })).items || [];
    await deleteItems(tables.functions, fns.map((f: any) => f.id));
    navigate('/');
    setIsDeleting(false);
  }

  return (
    <div className='p-4 md:px-8'>
      <Title>{t('project.config.title')}</Title>
      <Subtitle>{t('project.config.general.title')}</Subtitle>
      <div className='mt-4 max-w-3xl'>
        <label className='m-1 text-xs md:text-sm'>{t('general.name')}</label>
        <div className='flex items-center'>
          <Input textSize='small' extraclass='flex-1' value={newName} onChange={(e) => setNewName(e.target.value)} />
          <button disabled={isSavingName || !newName || newName === props.project.name || projects.map(p => p.name).includes(newName)} onClick={saveName} className='ml-2 text-sm bg-primary disabled:bg-gray-300 rounded-lg text-white py-2 px-4 flex items-center justify-center'>{isSavingName ? <Loading /> : t('general.save')}</button>
        </div>
      </div>
      <div className='mt-4 max-w-3xl'>
        <label className='m-1 text-xs md:text-sm'>{t('general.description')}</label>
        <div className='flex items-end'>
          <Textarea textSize='small' extraclass='flex-1' value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
          <button disabled={newDescription === props.project.description || isSavingDescription} onClick={saveDescription} className='ml-2 text-sm bg-primary disabled:bg-gray-300 rounded-lg text-white py-2 px-4 flex items-center justify-center'>{isSavingDescription ? <Loading /> : t('general.save')}</button>
        </div>
      </div>
      <Subtitle>{t('project.config.danger-zone.title')}</Subtitle>
      <div className='text-sm font-light mt-4'>{t('project.config.danger-zone.delete-project-warning')}</div>
      <button onClick={() => setShowDeleteAlert(true)} className='delete-project-button mt-2 text-sm bg-red-400 rounded-lg text-white py-2 px-8 flex items-center justify-center'>{t('project.config.danger-zone.delete-project')}</button>

      {showDeleteAlert && <AlertWrapper>
        <Alert
            extraclass='delete-project-alert'
            title={t('project.config.danger-zone.delete-project')}
            maxWidth={400}
            onClose={() => setShowDeleteAlert(false)}
            body={<div className='mx-1'>
              <div dangerouslySetInnerHTML={{ __html: t('project.config.danger-zone.delete-project-message', { name: props.project.name }) }} />
            </div>}
            bottom={<div className='flex'>
              <button onClick={deleteProject} disabled={isDeleting} className='disabled:bg-gray-300 flex items-center justify-center bg-red-400 text-white text-sm p-2 px-8 rounded-lg'>{isDeleting ? <Loading /> : t('general.delete')}</button>
            </div>}
          />  
      </AlertWrapper>}
    </div>
  )
}

export default Config